// api/invoice-send
// Freelancer resend of an admin-approved invoice to the customer.
// Backed by InvoiceV2Controller (send-summary / send) on the API.
import { get, post } from '../api.service';

/** One previous send of an invoice, newest first in `sentHistory`. */
export interface InvoiceSentHistoryItem {
    sentDate: string;
    toEmail: string | null;
    ccEmail: string | null;
    bccEmail: string | null;
    sentByName: string | null;
}

export interface InvoiceSendSummary {
    invoiceNumber: number;
    customerName: string | null;
    /** Address the invoice goes to if the freelancer proceeds. */
    recipientEmail: string | null;
    /** Carried over from the customer profile - shown but not editable. */
    ccEmail: string[];
    canSend: boolean;
    /** Why sending is blocked. Null when `canSend` is true. */
    blockReason: string | null;
    /**
     * Always true: the first email is sent by Invozio once admin approves, and a
     * resend is only accepted to a corrected address. Keep Send disabled until the
     * typed address differs from `lastSentToEmail`.
     */
    requiresEmailChange: boolean;
    /** Address the invoice was last sent to. Sending to it again is rejected (409). */
    lastSentToEmail: string | null;
    isEmailSent: boolean;
    lastSent: InvoiceSentHistoryItem | null;
    sentHistory: InvoiceSentHistoryItem[];
}

interface ApiResponse<T> {
    statusCode: number;
    message: string;
    response: T;
}

/**
 * Summary for the send modal: where the invoice will go, and when it was last
 * sent out and to which addresses.
 */
export const getInvoiceSendSummary = async (invoiceNumber: number | string): Promise<InvoiceSendSummary> => {
    const res: ApiResponse<InvoiceSendSummary> = await get(`/invoicev2/send-summary/${invoiceNumber}`);
    return res?.response;
};

/**
 * Sends the invoice to the customer. The address must differ from the one the
 * invoice was last sent to - the API rejects a resend to the same address with a
 * 409. The corrected address is saved to the customer record before the invoice
 * goes out, so later invoices use it too.
 */
export const sendInvoiceToCustomer = async (
    invoiceNumber: number | string,
    toEmail: string,
): Promise<ApiResponse<boolean>> => {
    return await post<ApiResponse<boolean>>(`/invoicev2/send/${invoiceNumber}`, { toEmail });
};
